// The Workflow page: ported from packages/workflow-engine-upstream/ui/src/App.tsx at commit bebc382.
// Laid out as the kit's reference screen: a run bar, then conversation, plan and answer, plus every
// model and tool call the engine made. Changed from the reference: our app shell (sidebar, header)
// around it, our authenticated API, no demo kit cases (their picker and expectation checks), the
// calls panel and its counts only when the API sends calls (an organization admin), and a message
// instead of an error line when the organization cannot run workflows (409) or its KG is warming (503).
// Also: a reply to an older question or rerun never replaces a newer one, a finished run is no
// longer watched, and the progress card is also reloaded every PROGRESS_EVERY_MS while the run is
// unfinished, since our stream carries only run events (the reference's also says when a call or
// a stage starts, which happen on whichever pod drives the run).
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from '@/i18n';
import AppSidebar from '@/components/AppSidebar';
import { PAGE_HEADER, PAGE_TITLE } from '@/components/layout/pageHeader';
import { AnswerPanel, type PickedStep } from './AnswerPanel';
import type { Period } from './contracts';
import { api, methodFile, unavailableOf, type RunState, type Unavailable } from './api';
import { followRun, type Followed } from './follow';
import { Calls } from './Calls';
import { Conversation } from './Conversation';
import { PlanPanel } from './PlanPanel';
import { ProgressCard } from './Progress';
import './workflow-engine.css';

const STATUS_CLASS: Record<string, string> = { finished: 'ok', running: 'on', parked: 'warn', awaiting_ask: 'warn', awaiting_approval: 'warn' };

/** The period the plan reads, to start a rerun from: the first gather step's period. */
function planPeriod(s: RunState): Period | undefined {
  const plan = s.trace.planVersions.at(-1);
  return plan?.steps.map((x) => (x.op as { period?: Period } | undefined)?.period).find((p) => p?.from && p.to);
}

/** F11: the same plan version on a new period. Shown once a run has run its plan. */
function Rerun({ s, onRerun }: { s: RunState; onRerun: (p: Period) => Promise<void> }) {
  const saved = planPeriod(s);
  const [from, setFrom] = useState(saved?.from ?? '');
  const [to, setTo] = useState(saved?.to ?? '');
  const [sending, setSending] = useState(false);
  const send = () => {
    setSending(true);
    void onRerun({ from: from.trim(), to: to.trim() }).finally(() => setSending(false));
  };
  return (
    <div className="bar rerun">
      <span className="note">Run plan v{s.planVersion} again, without planning, for</span>
      <input aria-label="Rerun from" value={from} onChange={(e) => setFrom(e.target.value)} placeholder="from, e.g. 2025-10-01T00:00:00Z" />
      <span className="note">to</span>
      <input aria-label="Rerun to" value={to} onChange={(e) => setTo(e.target.value)} placeholder="to, e.g. 2026-01-01T00:00:00Z" />
      <button type="button" disabled={sending || !from.trim() || !to.trim()} onClick={send}>
        {sending ? 'Starting…' : 'Rerun'}
      </button>
    </div>
  );
}

function RunBar({ s, onCancel }: { s: RunState; onCancel: () => Promise<void> }) {
  const [cancelling, setCancelling] = useState(false);
  // The budget counts up while the run runs, between its events.
  const [, setNow] = useState(0);
  useEffect(() => {
    if (s.status !== 'running') return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [s.status]);
  const model = s.calls.filter((c) => c.kind === 'model');
  const tools = s.calls.filter((c) => c.kind === 'tool');
  const seconds = (ms: number) => `${(ms / 1000).toFixed(1)} s`;
  const done = s.status === 'finished';
  const used = s.budget ? s.budget.usedMs + (s.status === 'running' ? Math.max(0, Date.now() - s.budget.asOf) : 0) : 0;
  return (
    <div className="bar">
      <b className="grow">{s.question}</b>
      <span className={`chip ${STATUS_CLASS[s.status] ?? ''}`}>
        {s.status.replace(/_/g, ' ')}
        {s.outcome ? ` · ${s.outcome}` : ''}
      </span>
      <span className="chip">Plan v{s.planVersion}</span>
      {s.budget && (
        <span className={`chip ${used > s.budget.totalMs ? 'bad' : ''}`} title="Running time; time parked or waiting for approval doesn't count">
          Budget {used < 10_000 ? (used / 1000).toFixed(1) : Math.round(used / 1000)} s of {Math.round(s.budget.totalMs / 1000)} s
        </span>
      )}
      {/* Calls come only to an organization admin; for anyone else the counts would read zero. */}
      {s.calls.length > 0 && (
        <>
          <span className="chip">
            Model {model.length} · {seconds(model.reduce((n, c) => n + c.durationMs, 0))}
          </span>
          <span className="chip">Tools {tools.length}</span>
        </>
      )}
      <button
        type="button"
        disabled={done || cancelling}
        title="Stops the run within one step timeout and answers from what it has"
        onClick={() => {
          setCancelling(true);
          void onCancel().finally(() => setCancelling(false));
        }}
      >
        {cancelling ? 'Cancelling…' : 'Cancel'}
      </button>
    </div>
  );
}

/** Why workflows cannot run here, in place of the screen's error line. */
function UnavailableNote({ u }: { u: Unavailable }) {
  if (u.kind === 'not_available') {
    return (
      <div role="alert" className="bar err">
        Workflows are not available for this organization{u.reason ? `: ${u.reason}` : '.'}
      </div>
    );
  }
  return (
    <div role="alert" className="bar err">
      {u.code === 'kg_still_warming'
        ? "This organization's knowledge graph is still loading. Try again in a minute."
        : u.code === 'engine_still_loading'
          ? "This organization's files are still being read for workflows. Try again in a minute."
          : `This organization's knowledge graph could not be loaded (${u.code}). Try again later.`}
    </div>
  );
}

/** A 409 or 503 refusal shows as why workflows cannot run; anything else as the error line. */
function failed(e: Error, setError: (m: string) => void, setUnavailable: (u: Unavailable) => void): void {
  const u = unavailableOf(e);
  if (u) setUnavailable(u);
  else setError(e.message);
}



/** The question, on several lines so a pasted question keeps every line; Enter asks, Shift-Enter starts a new line. */
function QuestionBox({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <textarea
      className="grow"
      rows={2}
      aria-label="Your question"
      value={value}
      placeholder="Ask about the plant…"
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        // Not while an input method is composing: there Enter confirms the characters.
        if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
          e.preventDefault();
          e.currentTarget.form?.requestSubmit();
        }
      }}
    />
  );
}

export default function WorkflowV4Page() {
  const { t } = useTranslation();
  const [question, setQuestion] = useState('');
  const [group, setGroup] = useState<string[]>([]);
  const [active, setActive] = useState(0);
  const [runs, setRuns] = useState<Record<string, RunState>>({});
  const [progress, setProgress] = useState<Record<string, Followed>>({});
  const [step, setStep] = useState<PickedStep | null>(null);
  // The newest question or rerun: a reply to an older one must not take over the screen.
  const request = useRef(0);
  const [error, setError] = useState('');
  const [unavailable, setUnavailable] = useState<Unavailable | null>(null);
  const [busy, setBusy] = useState(false);

  const fail = (e: Error) => failed(e, setError, setUnavailable);

  useEffect(() => {
    const offs = group.map((id) => followRun(id, setRuns, setProgress, (e: Error) => failed(e, setError, setUnavailable)));
    return () => offs.forEach((off) => off());
  }, [group]);

  const act = (fn: () => Promise<unknown>) => {
    setError('');
    setUnavailable(null);
    return fn().then(() => undefined).catch(fail);
  };
  const start = (ev: FormEvent) => {
    ev.preventDefault();
    // Enter submits the form even while the Ask button is disabled: the same conditions apply.
    if (busy || !question.trim()) return;
    setBusy(true);
    setRuns({});
    setProgress({});
    setStep(null);
    const mine = ++request.current;
    act(() =>
      api
        .start(question)
        .then(({ runIds }) => {
          if (mine !== request.current) return;
          setGroup(runIds);
          setActive(0);
        })
        .finally(() => setBusy(false)),
    );
  };
  const s = runs[group[active]];
  const prog = progress[group[active]];
  const follow = ({ runIds }: { runIds: string[] }) => {
    setRuns({});
    setProgress({});
    setStep(null);
    setGroup(runIds);
    setActive(0);
  };

  return (
    <div className="flex h-screen">
      <AppSidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <header className={PAGE_HEADER}>
          <span className={PAGE_TITLE}>{t('nav:menu.workflow', { defaultValue: 'Workflow' })}</span>
        </header>
        <div className="wf-engine flex-1 overflow-y-auto">
          <main>
            <form className="bar" onSubmit={start}>
              <QuestionBox value={question} onChange={setQuestion} />
              <button className="p" disabled={busy || !question.trim()}>
                {busy ? 'Reading…' : 'Ask'}
              </button>
            </form>
            {unavailable && <UnavailableNote u={unavailable} />}
            {error && <div className="bar err">{error}</div>}
            {prog && <ProgressCard key={prog.p.runId} p={prog.p} receivedAt={prog.at} />}
            {!s ? (
              <div className="col">
                <span className="note">
                  Type a question. The screen shows the ask, the plan, each step and the answer, and below them every model and tool call the engine makes.
                </span>
              </div>
            ) : (
              <>
                <RunBar s={s} onCancel={() => act(() => api.cancel(s.runId))} />
                {s.status === 'finished' && s.trace.steps.length > 0 && <Rerun key={s.runId} s={s} onRerun={(period) => {
                  const mine = ++request.current;
                  return act(() => api.rerun(s.runId, period).then((r) => mine === request.current && follow(r)));
                }} />}
                {group.length > 1 && (
                  <div className="tabs">
                    {group.map((id, i) => (
                      <button key={id} type="button" className={i === active ? 'chip on' : 'chip'} onClick={() => setActive(i)}>
                        Wording {i + 1}
                      </button>
                    ))}
                  </div>
                )}
                <div className="cols">
                  <Conversation s={s} onAnswer={(token, values) => act(() => api.answer(token, values))} onFile={async (file) => { await api.sendFile(s.runId, await methodFile(file)); }} />
                  <PlanPanel s={s} selected={step} onSelect={setStep} onApprove={() => act(() => api.approve(s.runId, s.planVersion))} onEdit={async (plan) => { await api.edit(s.runId, plan); }} />
                  <AnswerPanel s={s} step={step} />
                </div>
                {s.calls.length > 0 && <Calls s={s} />}
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
